// Settings store - navigation state for settings and workflow configuration
import { create } from 'zustand'
import { useAIStore } from './aiStore'

// ============================================================================
// TYPES
// ============================================================================

type SettingsTab = 'sparql' | 'ai-api' | 'workflows' | 'logs' | 'facets' | 'rules' | 'schema' | 'instances' | 'query' | 'journey'
type WorkflowSection = 'workflows' | 'prompts' | 'agents'

interface SettingsNavigationState {
  // Navigation to specific settings tab
  targetTab: SettingsTab | null
  // Navigation within workflows section
  targetSection: WorkflowSection | null
  // Specific prompt to open in editor
  targetPromptKey: string | null

  // Actions
  navigateToPrompt: (promptKey: string) => void
  navigateToTab: (tab: SettingsTab) => void
  clearNavigation: () => void
}

// ============================================================================
// NAVIGATION STORE
// ============================================================================

export const useSettingsNavigationStore = create<SettingsNavigationState>((set) => ({
  targetTab: null,
  targetSection: null,
  targetPromptKey: null,

  navigateToPrompt: (promptKey: string) => {
    set({
      targetTab: 'workflows',
      targetSection: 'prompts',
      targetPromptKey: promptKey,
    })
  },

  navigateToTab: (tab: SettingsTab) => {
    set({
      targetTab: tab,
      targetSection: null,
      targetPromptKey: null,
    })
  },

  clearNavigation: () => {
    set({
      targetTab: null,
      targetSection: null,
      targetPromptKey: null,
    })
  },
}))

// ============================================================================
// LEGACY SETTINGS WRAPPER (for backwards compatibility)
// ============================================================================

interface SettingsState {
  claudeApiKey: string
  isAIConfigured: boolean
}

/**
 * Hook to get settings from various stores
 * Currently wraps aiStore for API key access
 */
export function useSettingsStore(): SettingsState {
  const apiKey = useAIStore(state => state.apiKey)
  const isConfigured = useAIStore(state => state.isConfigured)

  return {
    claudeApiKey: apiKey,
    isAIConfigured: isConfigured,
  }
}
